/**
 * The guest's recovery EPISODE: one clock, running from the moment the transport was lost
 * until the guest either recovers or gives up and refreshes.
 *
 * Two things live here, and both exist because of the same defect.
 *
 * 1. The budget is an episode, not a ladder. Every layer used to bound only itself — an 8s
 *    reclaim ladder, then a refresh that reset the counter for a fresh 8s ladder, then two
 *    more app-level rebuilds each with their own — which is roughly 33s of frozen frame that
 *    no single number described. One clock spanning every attempt is what makes
 *    GUEST_TOTAL_RECOVERY_BUDGET_MS mean what it says.
 *
 * 2. The clock stops while the document is hidden. The budget is wall-clock but everything
 *    that evaluates it is a `setTimeout`, and mobile browsers throttle background timers to
 *    about one wake a minute. Measuring across a background gap therefore blows any budget
 *    shorter than the gap — which is all of them — so an app switch did not risk a terminal
 *    disconnect, it guaranteed one. Hidden time is not the user waiting.
 *
 * Kept out of guestPeerSession.ts deliberately: that file is already long, and this is a
 * separable concern with its own contract.
 */

import {
  createSuspendableClock,
  type SuspendableClock,
  type VisibilitySource,
  type VisibilityState,
} from './guestSessionLifecycle';
import {
  GUEST_HIDDEN_SESSION_HOLD_MS,
  GUEST_RECOVERY_DEADLINE_MS,
  GUEST_RECOVERY_HARD_CEILING_MS,
  GUEST_RESUME_SETTLE_MS,
  GUEST_TOTAL_RECOVERY_BUDGET_MS,
} from './webrtcLatencyContracts';
import type { GuestPeerLogger } from './guestPeerSessionTypes';

export type GuestRecoveryEpisodeOptions = {
  /**
   * Omitted → no suspension, no resume settle, no hidden-session expiry. That is the exact
   * pre-existing behaviour, and it is what keeps every non-browser embedder working.
   */
  visibilitySource?: VisibilitySource;
  /**
   * Called once, after the resume settle, when the document was hidden for longer than
   * GUEST_HIDDEN_SESSION_HOLD_MS. The session is no longer recoverable in place.
   */
  onHiddenSessionExpired: () => void;
  logger?: GuestPeerLogger;
  /** Injectable for tests; defaults to Date.now. */
  nowMs?: () => number;
};

export class GuestRecoveryEpisode {
  private readonly clock: SuspendableClock;
  private readonly nowMs: () => number;
  private readonly logger: GuestPeerLogger | undefined;
  private readonly onHiddenSessionExpired: () => void;
  private readonly visibilitySource: VisibilitySource | undefined;
  private unsubscribeVisibility: (() => void) | null = null;
  private resumeSettleTimer: ReturnType<typeof setTimeout> | null = null;
  /** Wall-clock instant the document went hidden; null while visible. */
  private hiddenSinceMs: number | null = null;
  private running = false;
  private disposed = false;
  /** Wall-clock the transport was last lost (resume clock origin); null while connected. */
  private transportLostAtMs: number | null = null;
  /** ms from transport-loss to media-resumed for the last completed resume. */
  private lastResumeLatencyMsValue: number | undefined;

  constructor(options: GuestRecoveryEpisodeOptions) {
    this.nowMs = options.nowMs ?? (() => Date.now());
    this.clock = createSuspendableClock(this.nowMs);
    this.logger = options.logger;
    this.onHiddenSessionExpired = options.onHiddenSessionExpired;
    this.visibilitySource = options.visibilitySource;

    if (!this.visibilitySource) return;
    if (this.visibilitySource.current() === 'hidden') this.markHidden();
    this.unsubscribeVisibility = this.visibilitySource.subscribe((state) =>
      this.handleVisibilityChange(state),
    );
  }

  /**
   * Begin the episode. Idempotent while one is already running: an episode spans every
   * attempt, so a second reclaim must inherit the first one's clock rather than restart it.
   */
  start(): void {
    if (this.disposed || this.running) return;
    this.running = true;
    this.clock.start();
    // start() clears the suspended flag by contract, so a document that is already hidden
    // has to re-suspend here — otherwise the very case this exists for (drop while
    // backgrounded) would run its budget at full speed.
    if (this.hiddenSinceMs !== null) this.clock.suspend();
  }

  /** Forget the episode, so the next failure is timed from its own start. */
  end(): void {
    this.running = false;
    this.clock.reset();
    // A reset clock is not a running one, but the suspension state has to survive: the
    // document is still hidden, and the next start() must know that.
    if (this.hiddenSinceMs !== null) this.clock.suspend();
  }

  isRunning(): boolean {
    return this.running;
  }

  /** Elapsed time in the current episode, EXCLUDING hidden spans. 0 when idle. */
  elapsedMs(): number {
    return this.running ? this.clock.elapsedMs() : 0;
  }

  /** Milliseconds left in the episode; 0 when spent, the full budget when idle. */
  remainingMs(): number {
    return Math.max(0, GUEST_TOTAL_RECOVERY_BUDGET_MS - this.elapsedMs());
  }

  /** True once a recovery making no progress has run past what a player will wait. */
  deadlineExpired(): boolean {
    if (!this.running) return false;
    return this.elapsedMs() >= GUEST_RECOVERY_DEADLINE_MS;
  }

  /** True once the episode has spent its whole budget, however healthy it still looks. */
  ceilingReached(): boolean {
    return this.elapsedMs() >= GUEST_RECOVERY_HARD_CEILING_MS;
  }

  /**
   * Mark the instant the transport was lost — the resume clock origin. Idempotent for a
   * single dropout: the FIRST loss in an episode is the one a player experiences.
   */
  markTransportLost(): void {
    if (this.transportLostAtMs !== null) return;
    this.transportLostAtMs = this.nowMs();
  }

  /**
   * Mark the instant inbound media resumed and compute the resume latency. Hidden spans are
   * excluded: the wall-clock gap is reduced by any time the document was backgrounded (a
   * hidden tab is not a waiting user).
   */
  markMediaResumed(): void {
    if (this.transportLostAtMs === null) return;
    const lostAt = this.transportLostAtMs;
    this.transportLostAtMs = null;
    let gap = this.nowMs() - lostAt;
    // If the document is currently hidden, the resume clock is suspended; do not charge the
    // in-progress hidden span against the resume latency.
    if (this.hiddenSinceMs !== null) {
      gap -= this.nowMs() - this.hiddenSinceMs;
    }
    this.lastResumeLatencyMsValue = Math.max(0, gap);
  }

  /** ms from transport-loss to media-resumed for the last completed resume; undefined before one. */
  lastResumeLatencyMs(): number | undefined {
    return this.lastResumeLatencyMsValue;
  }

  dispose(): void {
    this.disposed = true;
    this.running = false;
    this.clearResumeSettle();
    this.unsubscribeVisibility?.();
    this.unsubscribeVisibility = null;
    this.clock.reset();
  }

  /**
   * Resurrect the episode after dispose() so it can run again. Used when the session is
   * closed and then rejoined: close() disposes the episode, but the session instance is
   * reused so the episode must be restored rather than reconstructed.
   */
  resurrect(): void {
    if (!this.disposed) return;
    this.disposed = false;
    this.running = false;
    this.hiddenSinceMs = null;
    this.clock.reset();

    if (!this.visibilitySource) return;
    if (this.visibilitySource.current() === 'hidden') this.markHidden();
    this.unsubscribeVisibility = this.visibilitySource.subscribe((state) =>
      this.handleVisibilityChange(state),
    );
  }

  private handleVisibilityChange(state: VisibilityState): void {
    if (this.disposed) return;
    if (state === 'hidden') {
      this.markHidden();
      return;
    }
    this.markVisible();
  }

  private markHidden(): void {
    this.clearResumeSettle();
    // Idempotent by contract: mobile fires visibilitychange in bursts (audio focus, lock
    // screen, app switcher), and the FIRST hidden instant is the one that matters.
    if (this.hiddenSinceMs === null) this.hiddenSinceMs = this.nowMs();
    this.clock.suspend();
  }

  private markVisible(): void {
    const hiddenSinceMs = this.hiddenSinceMs;
    this.hiddenSinceMs = null;
    if (hiddenSinceMs === null && !this.clock.isSuspended()) return;

    const hiddenMs = hiddenSinceMs === null ? 0 : Math.max(0, this.nowMs() - hiddenSinceMs);
    this.clearResumeSettle();
    // Nothing is decided mid-wake: transport state sampled the instant a page resumes
    // describes the wake, not the link.
    this.resumeSettleTimer = setTimeout(() => {
      this.resumeSettleTimer = null;
      if (this.disposed) return;
      // Gone again while settling — the hidden span that matters is the new one.
      if (this.visibilitySource?.current() === 'hidden') return;

      if (hiddenMs > GUEST_HIDDEN_SESSION_HOLD_MS) {
        this.logger?.warn?.(
          `Document was hidden for ${hiddenMs}ms, past the ${GUEST_HIDDEN_SESSION_HOLD_MS}ms ` +
            'hold — the session is no longer recoverable in place',
        );
        this.end();
        this.onHiddenSessionExpired();
        return;
      }

      this.clock.resume();
    }, GUEST_RESUME_SETTLE_MS);
  }

  private clearResumeSettle(): void {
    if (this.resumeSettleTimer === null) return;
    clearTimeout(this.resumeSettleTimer);
    this.resumeSettleTimer = null;
  }
}
